import React, { useState, useRef } from "react";
import { ModalBuilder, Modal, Form } from "@redriver/cinnamon";
import { Button } from "semantic-ui-react";
import { TagInput } from "features/System/TagInput";
import classNames from "classnames";
import { OrganisationTypeAhead } from "features/Organisations/OrganisationTypeAhead";
import { useDispatch } from "react-redux";

const AddUsers = ({
  as: As = Button,
  onAdded,
  submitAction,
  submitParams,
  searchParams,
  title,
  allowExistingUsers,
  ...props
}) => {
  const dispatch = useDispatch();
  const tagInput = useRef(null);
  const [visible, setVisible] = useState(false);
  const [emails, setEmails] = useState([]);
  const [isEnteringEmails, setIsEnteringEmails] = useState(true);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [partialFailureMessage, setPartialFailureMessage] = useState(null);

  const reset = () => {
    setShowErrors(false);
    setEmails([]);
    setIsEnteringEmails(true);
    setHasAttemptedSubmit(false);
    setSubmitting(false);
    setPartialFailureMessage(null);
  };

  return (
    <React.Fragment>
      <As
        onClick={() => {
          setIsEnteringEmails(true);
          setVisible(true);
        }}
        {...props}
      />
      <ModalBuilder
        withForm
        visible={visible}
        initialData={{
          users: [],
        }}
        onLoaded={reset}
        onCancel={() => setVisible(false)}
        onCancelled={reset}
        renderModal={(modalProps, formProps, state) => (
          <Modal
            {...modalProps}
            header={title}
            size="tiny"
            submitLabel="Add"
            submitIcon="mail"
            submitting={submitting}
            onSubmit={async () => {
              setHasAttemptedSubmit(true);
              if (isEnteringEmails) {
                // an email typed without pressing return is still sat in the
                // input, so commit it before deciding there is nothing to add
                const currentEmails = tagInput.current
                  ? await tagInput.current.commitPendingEmail()
                  : emails;

                if (currentEmails.length > 0) {
                  const users = currentEmails.map((e) => {
                    const nameString = e.email.split("@")[0];
                    const names = nameString.split(/[\.\-_]/g);
                    return {
                      id: e.isNew ? null : e.id,
                      email: e.email,
                      firstName:
                        e.firstName ||
                        (names.length > 0
                          ? names[0][0].toUpperCase() + names[0].substring(1)
                          : ""),
                      lastName:
                        e.lastName ||
                        (names.length > 1
                          ? names[names.length - 1][0].toUpperCase() +
                            names[names.length - 1].substring(1)
                          : ""),
                      isAdmin: false,
                    };
                  });

                  formProps.onChange("users", users, (v) => ({
                    ...v,
                    users,
                  }));
                  setIsEnteringEmails(false);
                }
              } else if (state.formValid) {
                const attemptedUsers = formProps.value.users;

                setSubmitting(true);
                setPartialFailureMessage(null);
                dispatch(submitAction(formProps.value, submitParams))
                  .then(({ success, response }) => {
                    if (!success) {
                      return;
                    }

                    const failures = Array.isArray(response) ? response : [];
                    const failedEmails = new Set(
                      failures.map(({ email }) => email.toLowerCase())
                    );

                    if (onAdded) {
                      onAdded();
                    }

                    if (failedEmails.size === 0) {
                      setVisible(false);
                      return;
                    }

                    const remainingUsers = attemptedUsers
                      .filter((u) => failedEmails.has(u.email.toLowerCase()))
                      .map((u) => ({
                        ...u,
                        error: failures.find(
                          ({ email }) =>
                            email.toLowerCase() === u.email.toLowerCase()
                        )?.reason,
                      }));

                    const addedCount =
                      attemptedUsers.length - remainingUsers.length;
                    setPartialFailureMessage(
                      (addedCount > 0
                        ? `${addedCount} of ${attemptedUsers.length} users added. `
                        : "") +
                        `${remainingUsers.length} could not be added - review below.`
                    );

                    formProps.onChange("users", remainingUsers, (v) => ({
                      ...v,
                      users: remainingUsers,
                    }));
                  })
                  .finally(() => setSubmitting(false));
              } else {
                setShowErrors(true);
              }
            }}
          >
            <Form {...formProps} showErrors={showErrors}>
              <div
                style={{
                  marginBottom: "1rem",
                  display: isEnteringEmails ? "block" : "none",
                }}
              >
                <OrganisationTypeAhead
                  label="Organisation"
                  field="organisationId"
                  textField="organisationName"
                  fluid
                  placeholder="Optionally search for organisation"
                  clearable
                />
              </div>

              <TagInput
                ref={tagInput}
                allowExistingUsers={allowExistingUsers}
                label="Email Addresses"
                className="required"
                onUpdated={(items) => setEmails(items)}
                customError={
                  hasAttemptedSubmit && emails.length === 0
                    ? "One or more emails are required"
                    : null
                }
                searchParams={searchParams}
                style={{ display: isEnteringEmails ? "block" : "none" }}
              />

              {isEnteringEmails ? (
                <React.Fragment></React.Fragment>
              ) : (
                <React.Fragment>
                  <TextButton
                    onClick={() => {
                      setIsEnteringEmails(true);
                    }}
                  >
                    Back
                  </TextButton>
                  {partialFailureMessage && (
                    <p className="cin form-error">{partialFailureMessage}</p>
                  )}
                  {formProps.value.users.length === 0 && (
                    <p>No users to add.</p>
                  )}

                  <Form.Array
                    field="users"
                    arrayKey="email"
                    propagateUpdates="always"
                  >
                    {({ fields }) => (
                      <React.Fragment>
                        <div className="user-header">
                          <span className="email-label">{fields.email}</span>
                          <Form.ArrayRemover as={TextButton}>
                            Remove
                          </Form.ArrayRemover>
                        </div>
                        {fields.error && (
                          <p className="cin form-error">{fields.error}</p>
                        )}
                        <Form.Group widths="equal">
                          <Form.Input
                            placeholder="First Name"
                            field="firstName"
                            fluid
                            required
                            disabled={!!fields.id}
                          />
                          <Form.Input
                            placeholder="Last Name"
                            field="lastName"
                            fluid
                            required
                            disabled={!!fields.id}
                          />
                        </Form.Group>
                      </React.Fragment>
                    )}
                  </Form.Array>
                </React.Fragment>
              )}
            </Form>
          </Modal>
        )}
      />
    </React.Fragment>
  );
};

const TextButton = ({ children, className, ...props }) => (
  <a className={classNames("text-button", "pointer", className)} {...props}>
    {children}
  </a>
);

export default AddUsers;
